import { Building2, User } from "lucide-react";
import React from "react";

export type Plan = {
    id: string;
    title: string;
    description: string;
    icon: React.ReactElement;
    color: string;
    pricePerSeat: number;
    setupFee: number;
    features: string[];
    highlight?: string;
};

export const plans: Plan[] = [
    {
        id: "organization",
        title: "Organization",
        description:
            "Perfect for companies wanting to provide coaching benefits to their teams with our global coach network.",
        icon: React.createElement(Building2, { className: "w-8 h-8 text-blue-600" }),
        color: "blue",
        pricePerSeat: 22,
        setupFee: 0,
        features: [
            "Access to global B.R.I.E.F.S coach network",
            "All core coaching features and tools",
            "Admin dashboard for seat management",
            "Usage analytics and reporting",
            "Volume pricing: $22 (1-50), $17 (51-200), $13 (201+)",
        ],
    },
    {
        id: "practitioner",
        title: "Practitioner",
        description:
            "Build your own branded coaching platform with custom features, your own coaches, and complete control.",
        icon: React.createElement(User, { className: "w-8 h-8 text-purple-600" }),
        color: "purple",
        pricePerSeat: 20,
        setupFee: 1500,
        features: [
            "Custom branded coaching platform",
            "Your own custom domain",
            "Manage your own coach team",
            "Custom playlists and content library",
            "$1500 one-time setup + $20/user (1-30), $16/user (31+)",
        ],
        highlight: "Most Popular",
    },
];

// Function to calculate price per seat based on quantity tiers
export const getPricePerSeat = (planId: string, seatCount: number): number => {
    if (planId === "organization") {
        if (seatCount >= 1 && seatCount <= 50) return 22;
        if (seatCount >= 51 && seatCount <= 200) return 17;
        if (seatCount >= 201) return 13;
    } else if (planId === "practitioner") {
        if (seatCount >= 1 && seatCount <= 30) return 20;
        if (seatCount >= 31) return 16;
    }
    return 0;
};

// Function to get pricing tier info for display
export const getPricingTierInfo = (planId: string, seatCount: number): string => {
    if (planId === "organization") {
        if (seatCount >= 1 && seatCount <= 50) return "1-50 seats: $22/seat";
        if (seatCount >= 51 && seatCount <= 200) return "51-200 seats: $17/seat";
        if (seatCount >= 201) return "201+ seats: $13/seat";
    } else if (planId === "practitioner") {
        if (seatCount >= 1 && seatCount <= 30) return "1-30 seats: $20/seat";
        if (seatCount >= 31) return "31+ seats: $16/seat";
    }
    return "";
};

// Function to calculate monthly total
export const calculateMonthlyTotal = (planId: string, seatCount: number): number => {
    const pricePerSeat = getPricePerSeat(planId, seatCount);
    return seatCount * pricePerSeat;
};

// Function to calculate yearly total
export const calculateYearlyTotal = (planId: string, seatCount: number): number => {
    return calculateMonthlyTotal(planId, seatCount) * 12;
};

// Function to calculate total price including setup fee
export const calculateTotalPrice = (
    planId: string,
    seatCount: number,
    billingCycle: "monthly" | "yearly",
    setupFee: number
): number => {
    const monthlyTotal = calculateMonthlyTotal(planId, seatCount);
    const yearlyTotal = calculateYearlyTotal(planId, seatCount);
    
    return billingCycle === "monthly" ? monthlyTotal : yearlyTotal + setupFee;
};